export { captureAccessCodeFailure, captureEmailDeliveryFailure, captureException, captureMessage, capturePaymentFailure, captureProviderError, captureSecurityEvent, flush, log, redactLogData, reportClientPaymentError, setUser } from './chunk-JWV2IHGA.js';
import { setProviderInstance } from './chunk-Z2R6UR6G.js';
export { addBreadcrumb, clearContext, getProviderInstance, obfuscateEmail, obfuscatePhone, setClientContext, setProviderInstance, setRequestContext, setTenantContext, setUserContext, withIsolationScope } from './chunk-Z2R6UR6G.js';
import * as Sentry from '@sentry/react';

var SentryBrowserTransaction = class {
  constructor(span) {
    this.span = span;
  }
  get name() {
    return this.span?.name || "";
  }
  get op() {
    return this.span?.op;
  }
  get tags() {
    return this.span?.tags;
  }
  setStatus(status) {
    if (this.span) {
      this.span.setStatus(status);
    }
  }
  setData(key, value) {
    if (this.span) {
      this.span.setData(key, value);
    }
  }
  finish() {
    if (this.span) {
      this.span.end();
    }
  }
};
var SentryBrowserProvider = class {
  constructor() {
    this.initialized = false;
  }
  init(config) {
    if (this.initialized) {
      console.warn("Sentry already initialized");
      return;
    }
    const isDevelopment = config.environment === "development";
    if (config.enabled === false || isDevelopment && config.enabled !== true) {
      console.log(`\u{1F6AB} Monitoring disabled for ${config.service} (${config.environment})`);
      return;
    }
    if (!config.dsn) {
      console.warn("\u26A0\uFE0F Monitoring DSN not provided. Errors will not be tracked.");
      return;
    }
    Sentry.init({
      dsn: config.dsn,
      environment: config.environment || "development",
      release: config.release,
      dist: config.service,
      // Performance monitoring
      tracesSampleRate: config.tracesSampleRate ?? (isDevelopment ? 1 : 0.1),
      // Allow PII for debugging payment flow issues in staging
      sendDefaultPii: true,
      // Error filtering
      beforeSend: config.beforeSend || ((event, _hint) => {
        if (event.request?.headers) {
          delete event.request.headers.authorization;
          delete event.request.headers.cookie;
          delete event.request.headers["x-supabase-token"];
        }
        if (event.request?.data) {
          const data = JSON.stringify(event.request.data);
          if (/\d{13,19}/.test(data)) {
            console.warn("Potential credit card data detected in error, scrubbing...");
            return null;
          }
        }
        return event;
      }),
      ignoreErrors: config.ignoreErrors || [
        "Network request failed",
        "Failed to fetch",
        /^ResizeObserver loop/,
        "Non-Error promise rejection captured",
        "NetworkError",
        "AbortError"
      ],
      // React-specific integrations
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true
        })
      ]
    });
    this.initialized = true;
    console.log(`\u2705 Monitoring initialized for ${config.service} (${config.environment})`);
  }
  captureException(error, context) {
    return Sentry.captureException(error, {
      level: context?.level || "error",
      tags: context?.tags,
      extra: context?.extra,
      user: context?.user,
      contexts: {
        tenant: context?.tenant,
        client: context?.client,
        request: context?.request,
        provider: context?.provider,
        security: context?.security
      }
    });
  }
  captureMessage(message, level, context) {
    return Sentry.captureMessage(message, {
      level,
      tags: context?.tags,
      extra: context?.extra,
      user: context?.user,
      contexts: {
        tenant: context?.tenant,
        client: context?.client,
        request: context?.request,
        provider: context?.provider,
        security: context?.security
      }
    });
  }
  setUser(user) {
    Sentry.setUser(user);
  }
  setTenant(tenant) {
    Sentry.setContext("tenant", tenant);
  }
  setRequest(request) {
    Sentry.setContext("request", request);
  }
  addBreadcrumb(breadcrumb) {
    Sentry.addBreadcrumb({
      category: breadcrumb.category,
      message: breadcrumb.message,
      level: breadcrumb.level,
      data: breadcrumb.data,
      timestamp: breadcrumb.timestamp
    });
  }
  startTransaction(options) {
    const span = Sentry.startInactiveSpan({
      name: options.name,
      op: options.op,
      attributes: {
        ...options.tags,
        ...options.data
      }
    });
    if (!span) {
      throw new Error("Failed to create transaction span");
    }
    return new SentryBrowserTransaction(span);
  }
  setContext(key, value) {
    Sentry.setContext(key, value);
  }
  setTag(key, value) {
    Sentry.setTag(key, value);
  }
  async flush(timeout) {
    try {
      await Sentry.flush(timeout || 2e3);
      return true;
    } catch (error) {
      console.error("Failed to flush Sentry events:", error);
      return false;
    }
  }
};
var sentryBrowserProvider = new SentryBrowserProvider();

// web.ts
function initMonitoring(config) {
  sentryBrowserProvider.init(config);
  setProviderInstance(sentryBrowserProvider);
}

export { initMonitoring, sentryBrowserProvider };
//# sourceMappingURL=web.js.map
//# sourceMappingURL=web.js.map